import React from 'react';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import useVisitWishInbox from './useVisitWishInbox';
import VisitWishRequests from './VisitWishRequests';
import WishDetails from '../../Screens/VisitWishes/WishDetails';
import useWishActions from '../../Screens/VisitWishes/useWishActions';
import { VisitWish } from '../../Screens/VisitWishes/visitWishApi';
import { priorityReach, wishesOnDay } from '../../Screens/VisitWishes/wishFilters';
import Box from 'Components/Atoms/Box';
import './styles.scss';
import { CurrentStatus, Reservation } from 'types/reservations';
import TabCard from 'Components/Molecules/TabCard';
import ReservationCard from '../ReservationCard';
import ReservationGuestSearch from '../ReservationGuestSearch';
import ActionBar from './Components/ActionBar';
import { isTouchDevice, toCurrencyString } from 'utils/helper';
import Typography from 'Components/Atoms/Typography';
import Button from 'Components/Atoms/Button';
import { CircularProgress } from '@material-ui/core';
import {
  ReservationActionTypes,
  ReservationContext,
} from 'Contexts/ReservationContext';
import { ReservationShift } from 'types/shared/helper/shifts';
import WaitinglistCard from '../WaitinglistCard';
import ReservationTable, { isPrintShortcut } from '../ReservationTable';
import { TakeAwayOrder } from 'types/takeAway';
import useReservationList from 'CustomHooks/useReservationList';
import useKeyPressListener from 'CustomHooks/useKeyPressListener';
import useExperimentalFlags from 'CustomHooks/useExperimentalFlags';
import ShiftComments from './Components/ShiftComments';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

export type LeftSideBarProps = {
  reservations: Reservation[];
  /** The whole day, so the search also finds guests of another shift. */
  allReservations?: Reservation[];
  restaurantId: string;
  date: string;
  loading?: boolean;
  occassions: { id: string; title: string; hidden?: boolean }[];
  spaces: { id: string; name: string }[];
  currentShift?: ReservationShift | null;
  handleAction: (
    id: string,
    type: ReservationActionTypes,
    payload?: any
  ) => void;
  warnings?: string[];
  active: string | null;
  setActive: (id: string | null) => void;
};

type ReservationSidebarItem =
  | { type: 'same-custom-id'; key: string; reservation: Reservation }
  | { type: 'take-away'; key: string; order: TakeAwayOrder }
  | { type: 'reservation'; key: string; reservation: Reservation }
  | { type: 'empty'; key: string }
  | { type: 'loading'; key: string };

type ReservationListHeaderContext = {
  guestSearch: React.ReactNode;
  search: string | null;
  loadingSameCustomId: boolean;
};

const LeftSidebarScroller = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    {...props}
    ref={ref}
    className={['left-sidebar-scroll', className].filter(Boolean).join(' ')}
  />
));

LeftSidebarScroller.displayName = 'LeftSidebarScroller';

const VIRTUOSO_COMPONENTS = {
  Scroller: LeftSidebarScroller,
};

const ReservationListHeader: React.FC<{
  context?: ReservationListHeaderContext;
}> = ({ context }) => {
  if (!context) return null;

  return (
    <>
      {context.search && context.loadingSameCustomId && (
        <Box className="center">
          <CircularProgress />
        </Box>
      )}
      <ShiftComments />
    </>
  );
};

const RESERVATION_LIST_COMPONENTS = {
  ...VIRTUOSO_COMPONENTS,
  Header: ReservationListHeader,
  Footer: ({ context }: { context?: ReservationListHeaderContext }) => <>{context?.guestSearch}</>,
};

// The Button atom pads 1rem per side; half of it keeps both labels and the count on one line in the sidebar.
const MAIN_TAB_STYLE: React.CSSProperties = { cursor: 'pointer', padding: '0.5rem' };

/** A wish's sidebar takes the right-hand sidebar column; on a phone it is a mobile modal of its own. */
const WishDetailsPlacement: React.FC<{ phone: boolean }> = ({ phone, children }) =>
  phone ? <>{children}</> : <div className="visit-wish-sidebar-dock">{children}</div>;

const LeftSideBar: React.FC<LeftSideBarProps> = ({
  reservations,
  allReservations,
  date,
  loading = false,
  occassions,
  spaces,
  handleAction,
  warnings = [],
  active,
  restaurantId,
  setActive,
  currentShift = null,
}) => {
  const { t } = useTranslation('reservations');
  const inbox = useVisitWishInbox(restaurantId, { withSettings: true });
  const [showWaitingHistory, setShowWaitingHistory] = React.useState(false);
  const dayWishes = React.useMemo(() => wishesOnDay(inbox.wishes, date), [inbox.wishes, date]);
  const [openWishId, setOpenWishId] = React.useState<string | null>(null);
  // Only a wish for the calendar day stays open; with the app off there are none.
  const openWish = dayWishes.find((wish) => wish.id === openWishId) ?? null;
  React.useEffect(() => setOpenWishId(null), [date, restaurantId]);
  const phone = useMediaQuery('(max-width:600px)');
  const priorityOf = React.useCallback(
    (wish: VisitWish) => (inbox.settings ? priorityReach(wish, inbox.settings, date) : 'none'),
    [inbox.settings, date]
  );
  const wishActions = useWishActions({
    restaurantId,
    scopeKey: date,
    enabled: inbox.enabled,
    settings: inbox.settings,
    priorityOf,
    onUpdated: inbox.replace,
    reload: inbox.reload,
  });

  const hidePlacedGuests = !!useExperimentalFlags().data?.hidePlacedGuests;
  const reservationListRef = React.useRef<VirtuosoHandle>(null);
  const waitingListRef = React.useRef<VirtuosoHandle>(null);
  const lastFocusedReservationRef = React.useRef<string | null>(null);
  const lastFocusedWaitingListEntryRef = React.useRef<string | null>(null);

  const props = useReservationList({
    reservations,
    date,
    loading,
    occassions,
    spaces,
    handleAction,
    warnings,
    active,
    restaurantId,
    setActive,
    currentShift,
    allReservations,
  });

  const { tickets, setCurrentShift, currentTable } = React.useContext(ReservationContext);
  const hasShiftFilter = !!currentShift && !props.search;
  const [exporting, setexporting] = React.useState(false);

  const hasRequests = props.hasWaitingList || inbox.enabled;
  const showsRequests = hasRequests && props.tab === 'waitinglist';
  const canShowWaitinglist = inbox.enabled ? !!props.hasWaitingListEnabled : props.hasWaitingList;
  const waitinglistCount = canShowWaitinglist ? props.waitinglistCount ?? 0 : 0;
  const showsWaitingHistory = showWaitingHistory && canShowWaitinglist;

  React.useEffect(() => setShowWaitingHistory(false), [date, restaurantId, inbox.enabled, props.hasWaitingListEnabled]);
  React.useEffect(() => {
    if (props.currentWaitinglistEntry && props.value !== 0 && props.value != null) setShowWaitingHistory(true);
  }, [props.currentWaitinglistEntry, props.value]);

  // One sidebar beside the plan at a time: a wish gives way to a reservation, table or waiting-list entry.
  React.useEffect(() => {
    if (active || currentTable || props.currentWaitinglistEntry) setOpenWishId(null);
  }, [active, currentTable, props.currentWaitinglistEntry]);

  const openVisitWish = (wish: VisitWish) => {
    setActive(null);
    props.setcurrentWaitinglistEntry?.(null);
    setOpenWishId(wish.id);
  };

  useKeyPressListener(
    (e) => {
      if (!isPrintShortcut(e) || showsRequests) return;
      e.preventDefault();
      if (!e.repeat) setexporting(true);
    },
    { deps: [showsRequests] }
  );

  const warningSet = React.useMemo(() => new Set(warnings), [warnings]);

  const reservationItems = React.useMemo<ReservationSidebarItem[]>(() => {
    const items: ReservationSidebarItem[] = [];
    const keepReservationsAtTheTop =
      !!props.reservationSettings?.keepReservationsAtTheTop;
    const recentStartedCutoff = Date.now() - 5 * 60000;

    if (props.search && !props.loadingSameCustomId) {
      items.push(
        ...props.reservationsWithSameCustomId.map((reservation) => ({
          type: 'same-custom-id' as const,
          key: `same-custom-id-${reservation.id}`,
          reservation,
        }))
      );
    }

    if (props.pendingTakeAwayOrders.data) {
      items.push(
        ...props.pendingTakeAwayOrders.data.map((order) => ({
          type: 'take-away' as const,
          key: `take-away-${order.id}`,
          order,
        }))
      );
    }

    items.push(
      ...props.sortedReservations
        .filter(
          (res) =>
            !['paid', 'hasOrdered'].includes(res.currentStatus) &&
            (res.currentStatus !== CurrentStatus.SEATED ||
              res.partlySeated ||
              (keepReservationsAtTheTop &&
                res.startedAt &&
                res.startedAt > recentStartedCutoff)) &&
            (!hidePlacedGuests || !res.started)
        )
        .map((reservation) => ({
          type: 'reservation' as const,
          key: `reservation-${reservation.id}`,
          reservation,
        }))
    );

    if (!hidePlacedGuests) {
      items.push(
        ...props.sortedReservations
          .filter(
            (res) =>
              ['seated', 'paid', 'hasOrdered'].includes(res.currentStatus) &&
              !res.partlySeated &&
              (!keepReservationsAtTheTop ||
                !res.startedAt ||
                res.startedAt < recentStartedCutoff)
          )
          .map((reservation) => ({
            type: 'reservation' as const,
            key: `reservation-seated-${reservation.id}`,
            reservation,
          }))
      );
    }

    if (!props.sortedReservations.length && !loading) {
      items.push({ type: 'empty', key: 'empty' });
    }

    if (loading) {
      items.push({ type: 'loading', key: 'loading' });
    }

    return items;
  }, [
    props.search,
    props.loadingSameCustomId,
    props.reservationsWithSameCustomId,
    props.pendingTakeAwayOrders.data,
    props.sortedReservations,
    props.reservationSettings?.keepReservationsAtTheTop,
    hidePlacedGuests,
    loading,
  ]);

  React.useEffect(() => {
    if (!active) {
      lastFocusedReservationRef.current = null;
      return;
    }

    if (lastFocusedReservationRef.current === active) return;

    const index = reservationItems.findIndex(
      (item) =>
        (item.type === 'reservation' || item.type === 'same-custom-id') &&
        item.reservation.id === active
    );

    if (index >= 0) {
      lastFocusedReservationRef.current = active;
      reservationListRef.current?.scrollToIndex({
        index,
        align: 'center',
        behavior: isTouchDevice() ? 'auto' : 'smooth',
      });
    }
  }, [active, reservationItems]);

  React.useEffect(() => {
    if (!props.currentWaitinglistEntry) {
      lastFocusedWaitingListEntryRef.current = null;
      return;
    }

    if (
      lastFocusedWaitingListEntryRef.current === props.currentWaitinglistEntry
    )
      return;

    const index = props.filteredWaitingList.findIndex(
      (entry) => entry.id === props.currentWaitinglistEntry
    );

    if (index >= 0 && waitingListRef.current) {
      lastFocusedWaitingListEntryRef.current = props.currentWaitinglistEntry;
      waitingListRef.current.scrollToIndex({
        index,
        align: 'center',
        behavior: isTouchDevice() ? 'auto' : 'smooth',
      });
    }
  }, [props.currentWaitinglistEntry, props.filteredWaitingList, showsWaitingHistory]);

  const toggleAllReservations = React.useCallback(
    () => props.setshowAll((x) => !x),
    [props.setshowAll]
  );

  const reservationListContext = React.useMemo<ReservationListHeaderContext>(
    () => ({
      search: props.search,
      loadingSameCustomId: props.loadingSameCustomId,
      guestSearch: <ReservationGuestSearch restaurantId={restaurantId} date={date}
        search={props.search} reservations={allReservations || reservations} loading={loading} />,
    }),
    [props.search, props.loadingSameCustomId, restaurantId, date, allReservations, reservations, loading]
  );

  const renderReservationItem = React.useCallback(
    (_index: number, item: ReservationSidebarItem) => {
      if (item.type === 'same-custom-id') {
        return (
          <ReservationCard
            occassions={occassions}
            reservation={item.reservation}
            active={active === item.reservation.id}
            hasWarnings={warningSet.has(item.reservation.id)}
            hideOccassion={occassions.length <= 1}
            onClick={setActive}
            handleAction={handleAction}
            isAnotherDay
          />
        );
      }

      if (item.type === 'take-away') {
        return <TakeAwayOrderCard order={item.order} />;
      }

      if (item.type === 'reservation') {
        return (
          <ReservationCard
            occassions={occassions}
            reservation={item.reservation}
            active={active === item.reservation.id}
            hasWarnings={warningSet.has(item.reservation.id)}
            hideOccassion={occassions.length <= 1}
            onClick={setActive}
            handleAction={handleAction}
          />
        );
      }

      if (item.type === 'empty') {
        return (
          <Box
            fullSize
            style={{ minHeight: 100, maxHeight: 'calc(100% - 44px)' }}
            className="center flex-column"
            padding="md"
          >
            <Typography
              variant="text-3"
              color="subdued"
              translation="reservations"
              textAlign="center"
            >
              {hasShiftFilter
                ? 'No reservations for selected shift'
                : 'No corresponding reservation'}
            </Typography>
            {hasShiftFilter && (
              <>
                <Typography
                  variant="text-4"
                  color="subdued"
                  translation="reservations"
                  textAlign="center"
                  block
                  style={{ marginTop: 8 }}
                >
                  Shift filter active
                </Typography>
                <Button
                  variant="transparent"
                  translation="reservations"
                  onClick={() => setCurrentShift(null)}
                  style={{ marginTop: 8 }}
                >
                  Show all shifts
                </Button>
              </>
            )}
          </Box>
        );
      }

      return (
        <Box
          style={{ flexGrow: 10, minHeight: '100%' }}
          className="center"
          flex
          padding="md"
        >
          <CircularProgress />
        </Box>
      );
    },
    [active, handleAction, occassions, setActive, warningSet, hasShiftFilter, setCurrentShift]
  );

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
      className={`left-sidebar-root${hasRequests ? ' with-waitinglist' : ''}${inbox.enabled ? ' with-visit-wishes' : ''}`}
      id="leftSideBar"
    >
      {hasRequests && (
        <div className="left-sidebar-main-tabs" role="group" aria-label={t('Reservation book lists')}>
          <Button variant="transparent"
            className="left-sidebar-main-tab"
            aria-pressed={props.tab === 'reservations'}
            color={props.tab === 'reservations' ? 'secondary' : 'subdued'}
            onClick={() => {
              props.settab('reservations');
              props.setvalue(0);
            }}
            style={MAIN_TAB_STYLE}
          >
            Reservierungen
          </Button>
          <Button variant="transparent"
            className="left-sidebar-main-tab"
            aria-pressed={props.tab === 'waitinglist'}
            color={props.tab === 'waitinglist' ? 'secondary' : 'subdued'}
            onClick={() => {
              props.settab('waitinglist');
              setShowWaitingHistory(false);
              inbox.reload();
              props.setvalue(0);
            }}
            style={MAIN_TAB_STYLE}
          >
            {t('Requests')} ({waitinglistCount + dayWishes.length})
          </Button>
        </div>
      )}
      {(props.tab === 'reservations' || !hasRequests) && (
        <TabCard
          tabs={props.tabs as any}
          id="leftSideBarCard"
          className="left-sidebar"
          value={props.value}
          onChange={props.setvalue}
          hideHeader
          experimentalScroll={false}
          style={{
            borderRadius: 0,
            minHeight: inbox.enabled ? 0 : '100%',
            flexGrow: 10,
          }}
          bodyProps={{
            padding: 0,
            className: hasRequests
              ? 'left-sidebar-body with-waitinglist'
              : 'left-sidebar-body',
            style: { overflow: 'hidden' },
          }}
        >
          <Box
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <ActionBar
              {...{
                ...props,
                toggleAll: toggleAllReservations,
              }}
            />
            <Virtuoso
              ref={reservationListRef}
              style={{ flex: '1 1 auto', minHeight: 0 }}
              data={reservationItems}
              components={RESERVATION_LIST_COMPONENTS}
              context={reservationListContext}
              computeItemKey={(_index, item) => item.key}
              itemContent={renderReservationItem}
              increaseViewportBy={400}
            />
          </Box>
        </TabCard>
      )}
      {showsRequests && inbox.enabled && !showsWaitingHistory && (
        <VisitWishRequests
          restaurantId={restaurantId}
          date={date}
          wishes={inbox.wishes}
          loading={inbox.loading}
          failed={inbox.failed}
          hasMore={inbox.hasMore}
          onLoadMore={inbox.loadMore}
          selectedId={openWish?.id ?? null}
          onOpen={openVisitWish}
          actions={wishActions.wishActions}
          busyId={wishActions.busyId}
          otherRequestCount={canShowWaitinglist ? props.openWaitingList?.length ?? 0 : 0}
          selectedWaitingId={props.currentWaitinglistEntry}

        >
          {canShowWaitinglist && props.openWaitingList?.map((entry) => (
            <li key={`waitinglist-${entry.id}`} data-waiting-entry={entry.id}>
              <WaitinglistCard
                entry={entry}
                hideOccassion={occassions.length <= 1}
                showRequestType
                active={props.currentWaitinglistEntry === entry.id}
                onClick={() => {
                  props.setcurrentWaitinglistEntry(entry.id);
                  props.setcurrentReservation(null);
                }}
              />
            </li>
          ))}
        </VisitWishRequests>
      )}
      {showsRequests && inbox.enabled && showsWaitingHistory && (
        <button type="button" className="request-history-back" onClick={() => {
          props.setcurrentWaitinglistEntry(null);
          props.setvalue(0);
          setShowWaitingHistory(false);
        }}>{t('Back to open requests')}</button>
      )}
      {showsRequests && canShowWaitinglist && (!inbox.enabled || showsWaitingHistory) && (
        <TabCard
          tabs={props.waitinglist_tabs as any}
          id="leftSideBarCard"
          className="left-sidebar"
          value={props.value}
          onChange={props.setvalue}
          hideHeader
          experimentalScroll={false}
          style={{
            borderRadius: 0,
            minHeight: inbox.enabled ? 0 : '100%',
            flexGrow: 10,
          }}
          bodyProps={{
            padding: 0,
            className: 'left-sidebar-waitinglist-body',
            style: { overflow: 'hidden' },
          }}
        >
          <Virtuoso
            ref={waitingListRef}
            style={{ height: '100%' }}
            data={props.filteredWaitingList}
            components={VIRTUOSO_COMPONENTS}
            computeItemKey={(_index, entry) => entry.id}
            itemContent={(_index, entry) => (
              <WaitinglistCard
                entry={entry}
                hideOccassion={occassions.length <= 1}
                showRequestType
                active={props.currentWaitinglistEntry === entry.id}
                onClick={() => {
                  props.setcurrentWaitinglistEntry(entry.id);
                  props.setcurrentReservation(null);
                }}
              />
            )}
            increaseViewportBy={400}
          />
        </TabCard>
      )}
      {openWish && (
        <WishDetailsPlacement phone={phone}>
          <WishDetails
            wish={openWish}
            restaurantId={restaurantId}
            priority={priorityOf(openWish)}
            selectedDay={date}
            actions={wishActions.wishActions}
            priorityActions={wishActions.priorityActions}
            busy={wishActions.busyId === openWish.id}
            onClose={() => setOpenWishId(null)}
            presentation={phone ? 'modal' : 'panel'}
          />
        </WishDetailsPlacement>
      )}
      {inbox.enabled && wishActions.modal}
      {exporting && (
        <ReservationTable
          groups={props.search ? [] : props.groups}
          reservations={props.sortedReservations}
          handleAction={handleAction}
          active={active}
          occassions={occassions}
          warnings={props.warnings}
          onRowClick={setActive}
          spaces={spaces}
          fields={props.filters.columns}
          groupBy={props.search ? 'none' : props.filters.groupBy}
          hideEmptyTables={props.filters.hideEmptyTables}
          mode={
            props.filters.mode !== 'tickets' || tickets.data.length
              ? props.filters.mode
              : 'normal'
          }
          onExported={() => setexporting(false)}
        />
      )}
    </Box>
  );
};

export default LeftSideBar;

const TakeAwayOrderCard: React.FC<{
  order: TakeAwayOrder;
}> = ({ order }) => {
  const nameStr = order?.customer?.name || '';

  return (
    <Box
      underline
      id={order.id}
      elevation={0}
      className={`reservation-card resa-overdue`}
    >
      <Box className="space-between">
        <Typography
          variant="text-4"
          block
          className={'text-white'}
          style={{ marginTop: 4 }}
        >
          Bestellung zur Abholung
        </Typography>
      </Box>
      <Typography
        variant="text-3"
        weight="bold"
        block
        className={'text-white'}
        style={{
          marginTop: 3,
          whiteSpace: nameStr.length < 20 ? 'nowrap' : 'normal',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {nameStr}
      </Typography>
      <Typography
        variant="text-4"
        block
        className={'text-white'}
        style={{ marginTop: 4 }}
      >
        {order.time} • {toCurrencyString(order.total)}
      </Typography>
    </Box>
  );
};
